import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import tvShowService from '../../../services/tvShowService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const TvShowList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [tvShows, setTvShows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} TV shows`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });

  const getTvShows = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.search,
        ...params
      };
      
      console.log('Fetching TV shows with params:', requestParams);
      const response = await tvShowService.getAllTvShow(requestParams);
      console.log('TV Shows API Response:', response);
      
      if (response.data && response.data.status) {
        const tvShowsData = response.data.data?.data || [];
        const paginationData = response.data.data?.pagination || {};
        
        console.log('TV shows data:', tvShowsData);
        console.log('Pagination data:', paginationData);
        
        // Ensure tvShowsData is an array
        if (Array.isArray(tvShowsData)) {
          setTvShows(tvShowsData);
          setPagination(prev => ({
            ...prev,
            total: paginationData.total || tvShowsData.length,
            current: paginationData.currentPage || prev.current
          }));
        } else {
          console.error('TV shows data is not an array:', tvShowsData);
          setTvShows([]);
        }
      } else {
        message.error('Failed to fetch TV shows');
        setTvShows([]);
      }
    } catch (error) {
      console.error('Error fetching TV shows:', error);
      message.error('Failed to fetch TV shows');
      setTvShows([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageSize, pagination.current]);

  // Load TV shows data
  useEffect(() => {
    getTvShows();
  }, [getTvShows]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getTvShows({
      searchText: value,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getTvShows({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search
    });
  };

  // Handle add TV show
  const handleAddTvShow = () => {
    navigate('/TvShows/Shows/addTvShow');
  };

  // Handle edit TV show
  const handleEditTvShow = (tvShowId) => {
    navigate(`/TvShows/Shows/editTvShow/${tvShowId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'TV Show Name',
      dataIndex: 'tv_show_name',
      key: 'tv_show_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.tv_show_name.localeCompare(b.tv_show_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'No of Seasons',
      dataIndex: 'no_of_season',
      key: 'no_of_season',
      width: 150,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.no_of_season || 0) - (b.no_of_season || 0),
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text || 0}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'tv_show_is_active',
      key: 'tv_show_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditTvShow(record.tv_show_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage TV Shows</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="TV Shows List"
      desc="Manage TV shows and their details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              TV Shows ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTvShow}
              size="large"
            >
              Add Series
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search TV shows..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={tvShows}
          rowKey="tv_show_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No TV shows found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default TvShowList;